import type { QueryClient } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { show, updateNotification } from '@repletoryreach/sdk-ui/feedback';

/** How often to ask the service how the import is going. */
const POLL_MS = 2000;
/** Give up watching after this; the import itself carries on regardless. */
const MAX_WAIT_MS = 10 * 60 * 1000;

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Runs a CSV import and reports it in one toast, from queued through to a real
 * row count.
 *
 * `contacts.md` §4 item 5: the import was fire-and-forget. The user handed over
 * a file and got back a toast containing a UUID. There was no progress, no
 * completion notice and no error report, even though
 * `GET /v1/contacts/import/{id}` returns exactly those fields, so a run where
 * 400 of 500 rows failed validation was something you discovered by noticing
 * the list looked short.
 *
 * One notification is created and then updated in place, which is the case
 * `updateNotification` exists for and which nothing in tenant-ui used.
 */
export async function runImportWithProgress(
  file: File,
  t: Translate,
  qc: QueryClient,
  /** ISO 3166-1 alpha-2 to assume for numbers without a country code; the preview chose it. */
  defaultCountry?: string,
  /** Create a text field for every header that matched nothing, so those columns import too. */
  createFields?: boolean
): Promise<void> {
  const id = show({
    type: 'info',
    title: t('contacts.import.title'),
    message: t('contacts.import.uploading', { file: file.name }),
    // No auto-dismiss: this toast is the only progress the user gets.
    durationMs: 0
  });

  let jobId: string;
  try {
    const res = await contacts.importContacts(file, {
      ...(defaultCountry ? { default_country: defaultCountry } : {}),
      ...(createFields ? { create_fields: true } : {})
    });
    jobId = res.data.job_id;
    // A field created on the way in changes the contact form and the filter.
    if (res.data.created_fields?.length) qc.invalidateQueries({ queryKey: ['contact-fields'] });
    updateNotification(id, {
      message: t('contacts.import.queued', { total: res.data.total_rows ?? 0 })
    });
  } catch (e) {
    updateNotification(id, {
      type: 'error',
      message: e instanceof Error ? e.message : t('common.unknownError'),
      durationMs: 8000
    });
    return;
  }

  const startedAt = Date.now();
  for (;;) {
    await sleep(POLL_MS);

    let job;
    try {
      job = (await contacts.getImportJob(jobId)).data;
    } catch {
      // A single failed poll is not a failed import; keep watching.
      if (Date.now() - startedAt > MAX_WAIT_MS) break;
      continue;
    }

    if (job.state === 'running' || job.state === 'queued') {
      updateNotification(id, {
        message: t('contacts.import.running', {
          done: job.processed_rows ?? 0,
          total: job.total_rows ?? 0
        })
      });
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        updateNotification(id, {
          type: 'warning',
          message: t('contacts.import.stillRunning'),
          durationMs: 10_000
        });
        return;
      }
      continue;
    }

    qc.invalidateQueries({ queryKey: ['contacts'] });

    if (job.state === 'failed') {
      updateNotification(id, {
        type: 'error',
        title: t('contacts.import.failedTitle'),
        message: job.error || t('common.unknownError'),
        durationMs: 0
      });
      return;
    }

    // The report knows exactly how many rows the phone-number rule refused;
    // the plain count also covers rows the upsert could not place.
    const skipped = Math.max(job.skipped_rows ?? 0, job.report?.failed_count ?? 0);
    // Cells that did not fit their field's type were kept as text and counted;
    // a number column with "many" in it is something the person wants to know.
    const problems = job.report?.field_value_problems?.count ?? 0;
    // Rows that became email-only contacts (4 October 2026); not in the
    // SDK's generated report type yet.
    const emailOnly = (job.report as { email_only_rows?: number } | null)?.email_only_rows ?? 0;
    const summary = skipped > 0
      ? t('contacts.import.doneWithSkips', {
          inserted: job.inserted_rows ?? 0,
          updated: job.updated_rows ?? 0,
          skipped
        })
      : t('contacts.import.done', {
          inserted: job.inserted_rows ?? 0,
          updated: job.updated_rows ?? 0
        });
    updateNotification(id, {
      type: skipped > 0 || problems > 0 ? 'warning' : 'success',
      title: t('contacts.import.doneTitle'),
      // Say what was skipped. Reporting only the successes is how a user ends
      // up believing a broadcast will reach an audience that is 20% smaller
      // than they think.
      message: [
        summary,
        emailOnly > 0 ? t('contacts.import.emailOnlyRows', { count: emailOnly }) : '',
        problems > 0 ? t('contacts.import.doneFieldProblems', { count: problems }) : ''
      ].filter(Boolean).join(' '),
      durationMs: skipped > 0 || problems > 0 ? 0 : 8000
    });
    return;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
